<template>
  <div class="plan-card" :class="{ flipped: flipped }" @click="flipped = !flipped" title="Clique para virar a carteirinha">
    <div class="plan-card-inner">

      <div class="plan-card-face" :style="faceStyle">
        <div class="plan-card-overlay" v-if="planCard.card_image"></div>
        <div class="plan-card-content">
          <div class="plan-card-title">{{ planCard.plan_name }}</div>

          <div class="d-flex align-items-center gap-2">
            <img v-if="patient.patient_photo" :src="photo" alt="Foto do paciente" class="plan-card-photo">
            <div v-else class="plan-card-photo plan-card-initials">{{ initials }}</div>
            <div class="flex-grow-1 overflow-hidden">
              <div class="plan-card-name">{{ patient.patient_name }}</div>
              <div class="plan-card-detail">CPF: {{ maskCpf(patient.patient_cpf) }}</div>
            </div>
          </div>

          <div class="plan-card-number" :class="{ 'plan-card-number-empty': !cardNumber }">
            {{ cardNumber || 'Carteirinha não emitida' }}
          </div>

          <div class="d-flex align-items-end justify-content-between">
            <div class="d-flex gap-3">
              <div>
                <div class="plan-card-label">Emissão</div>
                <div class="plan-card-value">{{ monthYear(planCard.card_issued_at) }}</div>
              </div>
              <div>
                <div class="plan-card-label">Validade</div>
                <div class="plan-card-value">{{ monthYear(planCard.card_valid_until) }}</div>
              </div>
            </div>
            <span class="plan-card-badge">{{ planCard.holder ? 'Titular' : 'Dependente' }}</span>
          </div>
        </div>
      </div>

      <div class="plan-card-face back" :style="faceStyle">
        <div class="plan-card-overlay" v-if="planCard.card_image"></div>
        <div class="plan-card-content">
          <div class="d-flex align-items-start justify-content-between gap-2">
            <div class="plan-card-title">{{ planCard.place_name || planCard.plan_name }}</div>
            <span class="plan-card-badge" v-if="planCard.subscriber_status_description">
              {{ planCard.subscriber_status_description }}
            </span>
          </div>

          <div v-if="features.length > 0">
            <div class="plan-card-detail" v-for="(feature, index) in features" :key="index">
              <i class="fas fa-check-circle me-1"></i>{{ feature }}
            </div>
          </div>
          <div class="plan-card-detail" v-else>
            Apresente esta carteirinha na recepção para utilizar os benefícios do seu plano.
          </div>

          <div class="d-flex align-items-end justify-content-between">
            <div class="plan-card-value">{{ cardNumber }}</div>
            <i class="fas fa-sync-alt plan-card-detail"></i>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import strMask from "@/helpers/strMask";
import str from "@/helpers/str";

export default {
  name: "PlanCard",
  props: {
    patient: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      flipped: false,
    }
  },
  computed: {
    planCard() {
      return this.patient.plan_card || {};
    },
    features() {
      return (this.planCard.plan_features || []).slice(0, 3);
    },
    cardNumber() {
      if (!this.planCard.card_number) {
        return '';
      }

      return strMask.format(this.planCard.card_number, '#### #### #### ####');
    },
    photo() {
      return str.url(this.patient.patient_photo);
    },
    initials() {
      return str.initialsName(this.patient.patient_name);
    },
    faceStyle() {
      if (!this.planCard.card_image) {
        return {};
      }

      return {
        backgroundImage: `url('${this.planCard.card_image}')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      };
    },
  },
  watch: {
    patient() {
      this.flipped = false;
    },
  },
  methods: {
    maskCpf(value) {
      return strMask.it(value);
    },
    monthYear(value) {
      if (!value) {
        return '--/----';
      }

      const [year, month] = value.substring(0, 10).split('-');
      return `${month}/${year}`;
    },
  },
}
</script>

<style scoped>
.plan-card {
  perspective: 1200px;
  cursor: pointer;
  max-width: 420px;
  margin: 0 auto;
}

.plan-card-inner {
  position: relative;
  width: 100%;
  /* Proporção ISO/IEC 7810 ID-1 (85,6mm x 54mm), a mesma do PDF da carteirinha. */
  padding-top: 63.08%;
  transform-style: preserve-3d;
  transition: transform 0.6s;
}

.plan-card.flipped .plan-card-inner {
  transform: rotateY(180deg);
}

.plan-card-face {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  border-radius: 16px;
  overflow: hidden;
  color: #fff;
  background: linear-gradient(135deg, #780f5a 0%, #764ba2 100%);
  box-shadow: 0 6px 16px rgba(120, 15, 90, 0.24);
}

.plan-card-face.back {
  transform: rotateY(180deg);
}

.plan-card-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.3);
}

.plan-card-content {
  position: relative;
  height: 100%;
  padding: 14px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
}

.plan-card-title {
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.plan-card-photo {
  width: 38px;
  height: 38px;
  border-radius: 6px;
  object-fit: cover;
  background: rgba(255, 255, 255, 0.3);
}

.plan-card-initials {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  font-weight: 700;
}

.plan-card-name {
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.plan-card-detail {
  font-size: 0.72rem;
  opacity: 0.95;
}

.plan-card-number {
  font-size: 1.5rem;
  font-weight: 700;
  letter-spacing: 1px;
}

.plan-card-number-empty {
  font-size: 0.95rem;
}

.plan-card-label {
  font-size: 0.62rem;
  opacity: 0.85;
}

.plan-card-value {
  font-size: 0.75rem;
  font-weight: 700;
}

.plan-card-badge {
  font-size: 0.62rem;
  font-weight: 700;
  background: rgba(255, 255, 255, 0.3);
  padding: 3px 6px;
  border-radius: 4px;
  white-space: nowrap;
}
</style>
